/**
 * ThemeContext — Dark / Light mode toggle
 *
 * Persists preference in localStorage under "zap_theme".
 * Adds/removes ".dark" class on <html> so Tailwind v4's
 * `@custom-variant dark (&:is(.dark *))` kicks in.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
  isDark: boolean;
}

const STORAGE_KEY = "zap_theme";

function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    /* ignore */
  }
  // Respect OS preference
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

/** Safe default — prevents crash if rendered outside ThemeProvider */
const DEFAULT_THEME: ThemeContextType = {
  theme: getInitialTheme(),
  toggle: () => {},
  setTheme: () => {},
  isDark: getInitialTheme() === "dark",
};

const ThemeCtx = createContext<ThemeContextType>(DEFAULT_THEME);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Apply class to <html>
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
  }, []);

  return (
    <ThemeCtx.Provider value={{ theme, toggle, setTheme, isDark: theme === "dark" }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme(): ThemeContextType {
  return useContext(ThemeCtx);
}

/** Safe version — returns null outside provider (kept for backward compat) */
export function useThemeSafe(): ThemeContextType | null {
  return useContext(ThemeCtx);
}
